import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import axios from 'axios';
import { X, Monitor, Smartphone, Clock, MapPin } from 'lucide-react';
import Loader from './Loader';

function PieBar({ data, color = '#6B21A8' }) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) return <div style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>No data yet</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([key, val]) => (
        <div key={key}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: '0.8rem' }}>
            <span>{key}</span><span style={{ fontWeight: 600, color }}>{val}</span>
          </div>
          <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 3 }}>
            <div style={{ height: '100%', width: `${(val / total) * 100}%`, background: color, borderRadius: 3, transition: 'width 0.5s ease' }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// Inline Leaflet map rendered in an iframe to avoid React dependency issues
function ScanMap({ logs }) {
  const hasCoords = logs.filter(l => l.latitude != null && l.longitude != null);

  if (hasCoords.length === 0) {
    return (
      <div style={{ height: 220, background: 'var(--gray-50)', borderRadius: 10, border: '1px solid var(--gray-200)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)', gap: 8 }}>
        <MapPin size={28} style={{ opacity: 0.4 }} />
        <div style={{ fontSize: '0.82rem' }}>No location data yet</div>
        <div style={{ fontSize: '0.74rem', textAlign: 'center', maxWidth: 240, lineHeight: 1.5 }}>
          Location is collected via browser's geolocation API when users scan the QR code and allow location access.
        </div>
      </div>
    );
  }

  const markers = JSON.stringify(hasCoords.map(l => ({
    lat: l.latitude, lng: l.longitude,
    info: `${l.browser} · ${l.device} · ${new Date(l.scannedAt).toLocaleDateString()}`
  })));

  const html = `<!DOCTYPE html>
<html><head>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
<style>html,body,#map{margin:0;padding:0;width:100%;height:100%;}</style>
</head><body>
<div id="map"></div>
<script>
const markers = ${markers};
const map = L.map('map');
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',{attribution:'© OpenStreetMap'}).addTo(map);
const group = L.featureGroup();
markers.forEach(m => {
  const marker = L.circleMarker([m.lat, m.lng], {
    radius: 8, fillColor: '#7C3AED', color: '#4C1D95',
    weight: 2, opacity: 1, fillOpacity: 0.8
  }).bindPopup('<div style="font-family:sans-serif;font-size:12px"><b>Scan Location</b><br/>' + m.lat.toFixed(4) + ', ' + m.lng.toFixed(4) + '<br/><span style="color:#666">' + m.info + '</span></div>');
  group.addLayer(marker);
});
group.addTo(map);
if (markers.length === 1) {
  map.setView([markers[0].lat, markers[0].lng], 10);
} else {
  map.fitBounds(group.getBounds().pad(0.2));
}
<\/script>
</body></html>`;

  return (
    <iframe
      srcDoc={html}
      style={{ width: '100%', height: 220, border: 'none', borderRadius: 10, overflow: 'hidden' }}
      title="Scan locations map"
      sandbox="allow-scripts allow-same-origin"
    />
  );
}

// Tooltip map popup — portal-based, sticky hover + referer fix
function MapTooltip({ lat, lng }) {
  const [pos, setPos] = useState(null);
  const triggerRef = useRef();
  const hideTimer = useRef(null);

  if (lat == null || lng == null) return <span style={{ color: 'var(--gray-400)', fontSize: '0.78rem' }}>—</span>;

  const showTooltip = () => {
    clearTimeout(hideTimer.current);
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ left: rect.left + rect.width / 2, top: rect.top - 12 });
  };

  const startHide = () => { hideTimer.current = setTimeout(() => setPos(null), 150); };
  const cancelHide = () => clearTimeout(hideTimer.current);

  const html = `<!DOCTYPE html><html><head>
<link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
<script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
<style>html,body,#map{margin:0;padding:0;width:100%;height:100%;}</style>
</head><body><div id="map"></div><script>
const map=L.map('map',{zoomControl:true,attributionControl:false,scrollWheelZoom:true});
L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map);
map.setView([${lat},${lng}],13);
L.circleMarker([${lat},${lng}],{radius:8,fillColor:'#7C3AED',color:'#4C1D95',weight:2,opacity:1,fillOpacity:0.9}).addTo(map);
<\/script></body></html>`;

  const popup = pos ? ReactDOM.createPortal(
    <div
      onMouseEnter={cancelHide}
      onMouseLeave={startHide}
      style={{
        position: 'fixed', left: pos.left, top: pos.top,
        transform: 'translate(-50%, -100%)',
        zIndex: 99999, width: 500, height: 250,
        borderRadius: 12, overflow: 'hidden',
        boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
        border: '2px solid #9333EA', background: '#fff',
        pointerEvents: 'all', cursor: 'default',
      }}>
      <iframe srcDoc={html} style={{ width: '100%', height: '100%', border: 'none' }} title="loc" sandbox="allow-scripts" />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, textAlign: 'center', fontSize: '0.62rem', color: '#4C1D95', background: 'rgba(255,255,255,0.92)', padding: '3px 0', fontWeight: 600 }}>
        {lat.toFixed(5)}, {lng.toFixed(5)}
      </div>
      <div style={{ position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid #9333EA' }} />
    </div>,
    document.body
  ) : null;

  return (
    <>
      <span
        ref={triggerRef}
        onMouseEnter={showTooltip}
        onMouseLeave={startHide}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: 'var(--purple-800)', fontSize: '0.78rem', fontWeight: 500, background: 'var(--purple-100)', borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}
      >
        <MapPin size={11} />{lat.toFixed(3)}, {lng.toFixed(3)}
      </span>
      {popup}
    </>
  );
}

export default function StatsModal({ doctor, onClose }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`/api/stats/doctor/${doctor._id}`)
      .then(res => setStats(res.data))
      .finally(() => setLoading(false));
  }, [doctor._id]);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 700 }}>
        <div className="modal-header">
          <h2>Analytics — {doctor.name}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>

        {loading ? (
          <Loader />
        ) : (
          <>
            {/* Stat cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
              <div style={{ background: 'linear-gradient(135deg, #6B21A8, #9333EA)', borderRadius: 12, padding: '14px 18px', color: 'white' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700 }}>{stats.totalScans}</div>
                <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>Total QR Scans</div>
              </div>
              <div style={{ background: 'var(--gray-50)', borderRadius: 12, padding: '14px 18px', border: '1px solid var(--gray-200)' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#059669' }}>{stats.logsWithCoords?.length || 0}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>With Location</div>
              </div>
              <div style={{ background: 'var(--gray-50)', borderRadius: 12, padding: '14px 18px', border: '1px solid var(--gray-200)' }}>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#0891B2' }}>{Object.keys(stats.browsers || {}).length}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Browsers Used</div>
              </div>
            </div>

            {/* Map */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontWeight: 600, fontSize: '0.82rem' }}>
                <MapPin size={14} color="var(--purple-800)" /> Scan Locations Map
                <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)', fontWeight: 400, marginLeft: 4 }}>
                  ({stats.logsWithCoords?.length || 0} scans with location · hover rows below for individual map)
                </span>
              </div>
              <ScanMap logs={stats.logsWithCoords || []} />
            </div>

            {/* Browser & Device charts */}
            <div className="grid-2" style={{ marginBottom: 20 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontWeight: 600, fontSize: '0.82rem' }}>
                  <Monitor size={14} color="#0891B2" /> Browsers
                </div>
                <PieBar data={stats.browsers || {}} color="#0891B2" />
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontWeight: 600, fontSize: '0.82rem' }}>
                  <Smartphone size={14} color="#059669" /> Devices
                </div>
                <PieBar data={stats.devices || {}} color="#059669" />
              </div>
            </div>

            {/* Recent scans table with map tooltip on location column */}
            {stats.recentLogs?.length > 0 && (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontWeight: 600, fontSize: '0.82rem' }}>
                  <Clock size={14} color="var(--gray-600)" /> Recent Scans
                  <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)', fontWeight: 400 }}>— hover "Map Location" to see popup map</span>
                </div>
                <div style={{ border: '1px solid var(--gray-100)', borderRadius: 10, overflow: 'hidden' }}>
                  <div className="table-container" style={{ maxHeight: 240, overflow: 'auto' }}>
                    <table>
                      <thead>
                        <tr>
                          <th>IP Address</th>
                          <th>Map Location</th>
                          <th>Browser</th>
                          <th>Device</th>
                          <th>OS</th>
                          <th>Time</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stats.recentLogs.map(log => (
                          <tr key={log._id}>
                            <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{log.ipAddress}</td>
                            <td><MapTooltip lat={log.latitude} lng={log.longitude} /></td>
                            <td style={{ fontSize: '0.8rem' }}>{log.browser}</td>
                            <td style={{ fontSize: '0.8rem' }}>{log.device}</td>
                            <td style={{ fontSize: '0.8rem' }}>{log.os}</td>
                            <td style={{ fontSize: '0.75rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>{new Date(log.scannedAt).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// import React, { useState, useEffect, useRef } from 'react';
// import ReactDOM from 'react-dom';
// import axios from 'axios';
// import { X, Monitor, Smartphone, Clock, MapPin } from 'lucide-react';
// import Loader from './Loader';

// function PieBar({ data, color = '#6B21A8' }) {
//   const total = Object.values(data).reduce((a, b) => a + b, 0);
//   if (total === 0) return <div style={{ color: 'var(--gray-400)', fontSize: '0.8rem' }}>No data yet</div>;
//   return (
//     <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
//       {Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([key, val]) => (
//         <div key={key}>
//           <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3, fontSize: '0.8rem' }}>
//             <span>{key}</span><span style={{ fontWeight: 600, color }}>{val}</span>
//           </div>
//           <div style={{ height: 6, background: 'var(--gray-100)', borderRadius: 3 }}>
//             <div style={{ height: '100%', width: `${(val / total) * 100}%`, background: color, borderRadius: 3, transition: 'width 0.5s ease' }} />
//           </div>
//         </div>
//       ))}
//     </div>
//   );
// }

// // Inline Leaflet map rendered in an iframe to avoid React dependency issues
// function ScanMap({ logs }) {
//   const hasCoords = logs.filter(l => l.latitude != null && l.longitude != null);

//   if (hasCoords.length === 0) {
//     return (
//       <div style={{ height: 220, background: 'var(--gray-50)', borderRadius: 10, border: '1px solid var(--gray-200)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-400)', gap: 8 }}>
//         <MapPin size={28} style={{ opacity: 0.4 }} />
//         <div style={{ fontSize: '0.82rem' }}>No location data yet</div>
//         <div style={{ fontSize: '0.74rem', textAlign: 'center', maxWidth: 240, lineHeight: 1.5 }}>
//           Location is collected via browser's geolocation API when users scan the QR code and allow location access.
//         </div>
//       </div>
//     );
//   }

//   const markers = JSON.stringify(hasCoords.map(l => ({
//     lat: l.latitude, lng: l.longitude,
//     info: `${l.browser} · ${l.device} · ${new Date(l.scannedAt).toLocaleDateString()}`
//   })));

//   const html = `<!DOCTYPE html>
// <html><head>
// <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
// <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
// <style>html,body,#map{margin:0;padding:0;width:100%;height:100%;}</style>
// </head><body>
// <div id="map"></div>
// <script>
// const markers = ${markers};
// const map = L.map('map');
// L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',{attribution:'© OpenStreetMap contributors, © CARTO',subdomains:'abcd',maxZoom:20}).addTo(map);
// const group = L.featureGroup();
// markers.forEach(m => {
//   const marker = L.circleMarker([m.lat, m.lng], {
//     radius: 8, fillColor: '#7C3AED', color: '#4C1D95',
//     weight: 2, opacity: 1, fillOpacity: 0.8
//   }).bindPopup('<div style="font-family:sans-serif;font-size:12px"><b>Scan Location</b><br/>' + m.lat.toFixed(4) + ', ' + m.lng.toFixed(4) + '<br/><span style="color:#666">' + m.info + '</span></div>');
//   group.addLayer(marker);
// });
// group.addTo(map);
// if (markers.length === 1) {
//   map.setView([markers[0].lat, markers[0].lng], 10);
// } else {
//   map.fitBounds(group.getBounds().pad(0.2));
// }
// <\/script>
// </body></html>`;

//   return (
//     <iframe
//       srcDoc={html}
//       style={{ width: '100%', height: 220, border: 'none', borderRadius: 10, overflow: 'hidden' }}
//       title="Scan locations map"
//       sandbox="allow-scripts"
//     />
//   );
// }

// // Tooltip map popup — portal-based, sticky hover + referer fix
// function MapTooltip({ lat, lng }) {
//   const [pos, setPos] = useState(null);
//   const triggerRef = useRef();
//   const hideTimer = useRef(null);

//   if (lat == null || lng == null) return <span style={{ color: 'var(--gray-400)', fontSize: '0.78rem' }}>—</span>;

//   const showTooltip = () => {
//     clearTimeout(hideTimer.current);
//     if (!triggerRef.current) return;
//     const rect = triggerRef.current.getBoundingClientRect();
//     setPos({ left: rect.left + rect.width / 2, top: rect.top - 12 });
//   };

//   const startHide = () => { hideTimer.current = setTimeout(() => setPos(null), 150); };
//   const cancelHide = () => clearTimeout(hideTimer.current);

//   const html = `<!DOCTYPE html><html><head>
// <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"/>
// <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js"><\/script>
// <style>html,body,#map{margin:0;padding:0;width:100%;height:100%;}</style>
// </head><body><div id="map"></div><script>
// const map=L.map('map',{zoomControl:true,attributionControl:false,scrollWheelZoom:true});
// L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',{subdomains:'abcd',maxZoom:20}).addTo(map);
// map.setView([${lat},${lng}],13);
// L.circleMarker([${lat},${lng}],{radius:8,fillColor:'#7C3AED',color:'#4C1D95',weight:2,opacity:1,fillOpacity:0.9}).addTo(map);
// <\/script></body></html>`;

//   const popup = pos ? ReactDOM.createPortal(
//     <div
//       onMouseEnter={cancelHide}
//       onMouseLeave={startHide}
//       style={{
//       position: 'fixed', left: pos.left, top: pos.top,
//       transform: 'translate(-50%, -100%)',
//       zIndex: 99999, width: 500, height: 250,
//       borderRadius: 12, overflow: 'hidden',
//       boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
//       border: '2px solid #9333EA', background: '#fff',
//       pointerEvents: 'all', cursor: 'default',
//     }}>
//       <iframe srcDoc={html} style={{ width: '100%', height: '100%', border: 'none' }} title="loc" sandbox="allow-scripts" />
//       <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, textAlign: 'center', fontSize: '0.62rem', color: '#4C1D95', background: 'rgba(255,255,255,0.92)', padding: '3px 0', fontWeight: 600 }}>
//         {lat.toFixed(5)}, {lng.toFixed(5)}
//       </div>
//       <div style={{ position: 'absolute', bottom: -8, left: '50%', transform: 'translateX(-50%)', width: 0, height: 0, borderLeft: '8px solid transparent', borderRight: '8px solid transparent', borderTop: '8px solid #9333EA' }} />
//     </div>,
//     document.body
//   ) : null;

//   return (
//     <>
//       <span
//         ref={triggerRef}
//         onMouseEnter={showTooltip}
//         onMouseLeave={startHide}
//         style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer', color: 'var(--purple-800)', fontSize: '0.78rem', fontWeight: 500, background: 'var(--purple-100)', borderRadius: 6, padding: '2px 8px', whiteSpace: 'nowrap' }}
//       >
//         <MapPin size={11} />{lat.toFixed(3)}, {lng.toFixed(3)}
//       </span>
//       {popup}
//     </>
//   );
// }

// export default function StatsModal({ doctor, onClose }) {
//   const [stats, setStats] = useState(null);
//   const [loading, setLoading] = useState(true);

//   useEffect(() => {
//     axios.get(`/api/stats/doctor/${doctor._id}`)
//       .then(res => setStats(res.data))
//       .finally(() => setLoading(false));
//   }, [doctor._id]);

//   return (
//     <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
//       <div className="modal" style={{ maxWidth: 700 }}>
//         <div className="modal-header">
//           <h2>Analytics — {doctor.name}</h2>
//           <button className="modal-close" onClick={onClose}><X size={20} /></button>
//         </div>

//         {loading ? (
//           <Loader />
//         ) : (
//           <>
//             {/* Stat cards */}
//             <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 20 }}>
//               <div style={{ background: 'linear-gradient(135deg, #6B21A8, #9333EA)', borderRadius: 12, padding: '14px 18px', color: 'white' }}>
//                 <div style={{ fontSize: '2rem', fontWeight: 700 }}>{stats.totalScans}</div>
//                 <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>Total QR Scans</div>
//               </div>
//               <div style={{ background: 'var(--gray-50)', borderRadius: 12, padding: '14px 18px', border: '1px solid var(--gray-200)' }}>
//                 <div style={{ fontSize: '2rem', fontWeight: 700, color: '#059669' }}>{stats.logsWithCoords?.length || 0}</div>
//                 <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>With Location</div>
//               </div>
//               <div style={{ background: 'var(--gray-50)', borderRadius: 12, padding: '14px 18px', border: '1px solid var(--gray-200)' }}>
//                 <div style={{ fontSize: '2rem', fontWeight: 700, color: '#0891B2' }}>{Object.keys(stats.browsers || {}).length}</div>
//                 <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Browsers Used</div>
//               </div>
//             </div>

//             {/* Map */}
//             <div style={{ marginBottom: 20 }}>
//               <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontWeight: 600, fontSize: '0.82rem' }}>
//                 <MapPin size={14} color="var(--purple-800)" /> Scan Locations Map
//                 <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)', fontWeight: 400, marginLeft: 4 }}>
//                   ({stats.logsWithCoords?.length || 0} scans with location · hover rows below for individual map)
//                 </span>
//               </div>
//               <ScanMap logs={stats.logsWithCoords || []} />
//             </div>

//             {/* Browser & Device charts */}
//             <div className="grid-2" style={{ marginBottom: 20 }}>
//               <div>
//                 <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontWeight: 600, fontSize: '0.82rem' }}>
//                   <Monitor size={14} color="#0891B2" /> Browsers
//                 </div>
//                 <PieBar data={stats.browsers || {}} color="#0891B2" />
//               </div>
//               <div>
//                 <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontWeight: 600, fontSize: '0.82rem' }}>
//                   <Smartphone size={14} color="#059669" /> Devices
//                 </div>
//                 <PieBar data={stats.devices || {}} color="#059669" />
//               </div>
//             </div>

//             {/* Recent scans table with map tooltip on location column */}
//             {stats.recentLogs?.length > 0 && (
//               <div>
//                 <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, fontWeight: 600, fontSize: '0.82rem' }}>
//                   <Clock size={14} color="var(--gray-600)" /> Recent Scans
//                   <span style={{ fontSize: '0.72rem', color: 'var(--gray-400)', fontWeight: 400 }}>— hover "Map Location" to see popup map</span>
//                 </div>
//                 <div style={{ border: '1px solid var(--gray-100)', borderRadius: 10, overflow: 'hidden' }}>
//                   <div className="table-container" style={{ maxHeight: 240, overflow: 'auto' }}>
//                     <table>
//                       <thead>
//                         <tr>
//                           <th>IP Address</th>
//                           <th>Map Location</th>
//                           <th>Browser</th>
//                           <th>Device</th>
//                           <th>OS</th>
//                           <th>Time</th>
//                         </tr>
//                       </thead>
//                       <tbody>
//                         {stats.recentLogs.map(log => (
//                           <tr key={log._id}>
//                             <td style={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>{log.ipAddress}</td>
//                             <td><MapTooltip lat={log.latitude} lng={log.longitude} /></td>
//                             <td style={{ fontSize: '0.8rem' }}>{log.browser}</td>
//                             <td style={{ fontSize: '0.8rem' }}>{log.device}</td>
//                             <td style={{ fontSize: '0.8rem' }}>{log.os}</td>
//                             <td style={{ fontSize: '0.75rem', color: 'var(--gray-400)', whiteSpace: 'nowrap' }}>{new Date(log.scannedAt).toLocaleString()}</td>
//                           </tr>
//                         ))}
//                       </tbody>
//                     </table>
//                   </div>
//                 </div>
//               </div>
//             )}
//           </>
//         )}
//       </div>
//     </div>
//   );
// }
